import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldAlert, RefreshCw, AlertCircle, Network, Globe, Bell, FileText, History, GitBranch } from 'lucide-react';
import { useApi } from '../contexts/ApiContext';
import { format } from 'date-fns';
import { ApiResponse, FootprintData, FootprintCluster, FootprintAlert, FootprintWhoisRow, FootprintReverseWhois } from '../types';
import FootprintGraph from '../components/FootprintGraph';
import EmptyState from '../components/ui/EmptyState';
import { SkeletonCard } from '../components/ui/Skeleton';

type Tab = 'overview' | 'clusters' | 'alerts' | 'whois' | 'history';

const SEVERITY_STYLES: Record<string, string> = {
  critical: 'text-red-700 bg-red-50 border-red-200',
  high: 'text-orange-700 bg-orange-50 border-orange-200',
  medium: 'text-amber-700 bg-amber-50 border-amber-200',
  low: 'text-slate-600 bg-slate-50 border-slate-200',
  info: 'text-blue-700 bg-blue-50 border-blue-200',
};
const SEVERITY_RANK: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3, info: 4 };

function SeverityBadge({ severity }: { severity: string }) {
  const cls = SEVERITY_STYLES[severity] ?? SEVERITY_STYLES.low;
  return <span className={`inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-md border capitalize ${cls}`}>{severity}</span>;
}

function StatCard({ label, value, tone }: { label: string; value: string | number; tone?: 'red' | 'green' | 'default' }) {
  const valueColor = tone === 'red' ? 'text-red-600' : tone === 'green' ? 'text-emerald-600' : 'text-slate-900';
  return (
    <div className="card px-5 py-4">
      <div className="text-xs font-medium text-slate-500 uppercase tracking-wider">{label}</div>
      <div className={`text-3xl font-bold mt-1 ${valueColor}`}>{value}</div>
    </div>
  );
}

export default function FootprintAuditPage() {
  const { fetchApi, isAdmin } = useApi();
  const [data, setData] = useState<FootprintData | null>(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('overview');
  const [search, setSearch] = useState('');
  const [severity, setSeverity] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchApi<ApiResponse<FootprintData>>('/footprint');
      if (res.data) setData(res.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load footprint audit');
    } finally {
      setLoading(false);
    }
  }, [fetchApi]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh while a (reverse) audit runs in the background.
  useEffect(() => {
    if (!data?.running) return;
    const id = setInterval(load, 4000);
    return () => clearInterval(id);
  }, [data?.running, load]);

  const runAudit = useCallback(async (reverse: boolean) => {
    setRunning(true);
    setError(null);
    try {
      await fetchApi('/footprint/run', { method: 'POST', body: JSON.stringify({ reverse }) });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Audit failed');
    } finally {
      setRunning(false);
    }
  }, [fetchApi, load]);

  const run = data?.latest_run ?? null;
  const busy = running || !!data?.running;
  const q = search.trim().toLowerCase();

  const exposedRows = useMemo(
    () => (data?.whois ?? []).filter((w) => !w.privacy_protected && (w.registrant_name || w.registrant_org || w.registrant_email)),
    [data]
  );

  const nonGenericClusters = useMemo(
    () => (data?.clusters ?? []).filter((c) => !c.is_generic).sort((a, b) => (SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity]) || b.domain_count - a.domain_count),
    [data]
  );

  const matchesSearch = (...vals: (string | null | undefined)[]) => !q || vals.some((v) => v && v.toLowerCase().includes(q));
  const matchesSeverity = (s: string) => !severity || s === severity;

  const filteredClusters = nonGenericClusters.filter((c) => matchesSeverity(c.severity) && matchesSearch(c.signal_value, ...c.domains));
  const filteredAlerts = (data?.alerts ?? [])
    .filter((a) => matchesSeverity(a.severity) && matchesSearch(a.domain_name, a.title, a.description))
    .sort((a, b) => SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity]);
  const filteredWhois = (data?.whois ?? []).filter((w) => matchesSearch(w.domain_name, w.registrant_name, w.registrant_org, w.registrant_email));

  const TABS: { key: Tab; label: string; icon: typeof Globe; count?: number }[] = [
    { key: 'overview', label: 'Overview', icon: ShieldAlert, count: run?.domains_exposed },
    { key: 'clusters', label: 'Clusters', icon: Network, count: nonGenericClusters.length },
    { key: 'alerts', label: 'Alerts', icon: Bell, count: data?.alerts.length },
    { key: 'whois', label: 'WHOIS Data', icon: FileText, count: data?.whois.length },
    { key: 'history', label: 'Audit History', icon: History, count: data?.runs.length },
  ];

  return (
    <div className="space-y-6">
      {/* Header banner */}
      <div className="rounded-xl bg-gradient-to-r from-ink-800 to-ink-600 text-white px-6 py-5 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-white/15 flex items-center justify-center">
            <ShieldAlert className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-xl font-bold">Footprint Audit</h1>
            <p className="text-sm text-white/70">WHOIS fingerprint exposure analysis — detect shared infrastructure & identity leaks</p>
          </div>
        </div>
        {isAdmin && (
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={() => runAudit(true)} disabled={busy} title="Also chase exposed identities through Whoxy reverse-WHOIS (uses credits)" className="btn bg-white/15 text-white border border-white/30 hover:bg-white/25 disabled:opacity-60">
              <GitBranch className="h-4 w-4 mr-1.5" />
              + Reverse WHOIS
            </button>
            <button onClick={() => runAudit(false)} disabled={busy} className="btn bg-white text-ink-900 hover:bg-ink-300 disabled:opacity-60">
              <RefreshCw className={`h-4 w-4 mr-1.5 ${busy ? 'animate-spin' : ''}`} />
              {busy ? 'Running…' : 'Run Audit'}
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {loading && !data ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {Array.from({ length: 5 }, (_, i) => <SkeletonCard key={i} lines={2} />)}
          </div>
          <SkeletonCard lines={8} />
        </div>
      ) : !run ? (
        <div className="card">
          <EmptyState
            icon={ShieldAlert}
            title="No audit has been run yet"
            description="Run an audit to analyse your portfolio's WHOIS footprint."
            action={isAdmin && (
              <button onClick={() => runAudit(false)} disabled={busy} className="btn btn-primary">
                <RefreshCw className={`h-4 w-4 mr-1.5 ${busy ? 'animate-spin' : ''}`} />
                Run First Audit
              </button>
            )}
          />
        </div>
      ) : (
        <>
          {/* Stat cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <StatCard label="Domains Checked" value={run.domains_checked.toLocaleString()} />
            <StatCard label="Exposed" value={run.domains_exposed.toLocaleString()} tone={run.domains_exposed > 0 ? 'red' : 'green'} />
            <StatCard label="Clusters" value={run.clusters_found.toLocaleString()} />
            <StatCard label="Protected" value={`${run.summary?.protectedPct ?? 0}%`} tone="green" />
            <StatCard label="Last Audit" value={format(new Date(run.started_at), 'MMM d, yyyy')} />
          </div>

          {/* Tabs */}
          <div className="border-b border-slate-200 flex flex-wrap gap-1">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px inline-flex items-center gap-1.5 ${
                  tab === t.key ? 'border-brand-600 text-brand-700' : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                {t.label}
                {t.count != null && <span className="text-xs bg-slate-100 text-slate-600 rounded-full px-1.5 py-0.5">{t.count}</span>}
              </button>
            ))}
          </div>

          {/* Filter bar (not on history) */}
          {tab !== 'history' && (
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search domains, registrant names, orgs, emails…"
                className="input flex-1"
              />
              {(tab === 'clusters' || tab === 'alerts') && (
                <select value={severity} onChange={(e) => setSeverity(e.target.value)} className="input sm:w-44">
                  <option value="">All severities</option>
                  <option value="critical">Critical</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              )}
            </div>
          )}

          {/* Tab content */}
          {tab === 'overview' && (
            <div className="space-y-4">
              <ExposedTable rows={exposedRows.filter((w) => matchesSearch(w.domain_name, w.registrant_name, w.registrant_org, w.registrant_email))} />
              <FootprintGraph clusters={nonGenericClusters} reverse={data?.reverse_whois ?? []} />
              {(data?.reverse_whois.length ?? 0) > 0 && <ReverseConnections rows={data!.reverse_whois} />}
            </div>
          )}
          {tab === 'clusters' && <ClustersList clusters={filteredClusters} />}
          {tab === 'alerts' && <AlertsTable alerts={filteredAlerts} />}
          {tab === 'whois' && <WhoisTable rows={filteredWhois} />}
          {tab === 'history' && <HistoryTable runs={data?.runs ?? []} />}
        </>
      )}
    </div>
  );
}

function ExposedTable({ rows }: { rows: FootprintWhoisRow[] }) {
  return (
    <div className="card overflow-hidden">
      <div className="px-6 py-3 border-b border-slate-200">
        <h2 className="text-sm font-semibold text-red-700">Exposed Domains ({rows.length})</h2>
        <p className="text-xs text-slate-500">These domains have non-redacted WHOIS data, discoverable via public WHOIS or reverse lookups.</p>
      </div>
      <Table head={['Domain', 'Name', 'Company', 'Email', 'NS']}>
        {rows.map((w) => (
          <tr key={w.id} className="hover:bg-slate-50">
            <td className="px-4 py-2 font-medium">{w.domain_id ? <Link to={`/domains/${w.domain_id}`} className="text-brand-600 hover:text-brand-800">{w.domain_name}</Link> : w.domain_name}</td>
            <td className="px-4 py-2 text-slate-600">{w.registrant_name || '—'}</td>
            <td className="px-4 py-2 text-slate-600">{w.registrant_org || '—'}</td>
            <td className="px-4 py-2 text-slate-600">{w.registrant_email || '—'}</td>
            <td className="px-4 py-2 text-slate-400 text-xs font-mono truncate max-w-[160px]" title={w.nameservers.join(', ')}>{w.nameservers[0] || '—'}</td>
          </tr>
        ))}
        {rows.length === 0 && <EmptyRow span={5} text="No exposed domains — all checked WHOIS is redacted. 🎉" />}
      </Table>
    </div>
  );
}

function ReverseConnections({ rows }: { rows: FootprintReverseWhois[] }) {
  return (
    <div className="card overflow-hidden">
      <div className="px-6 py-3 border-b border-slate-200">
        <h2 className="text-sm font-semibold text-slate-800 flex items-center gap-1.5"><GitBranch className="h-4 w-4 text-slate-400" /> Reverse-WHOIS Connections</h2>
        <p className="text-xs text-slate-500">Domains across the registry registered under your exposed identities. "In inventory" are ones you already own; the rest are external links a competitor could chain to.</p>
      </div>
      <Table head={['Identity', 'Type', 'From domain', 'Total found', 'In inventory']}>
        {rows.map((r) => (
          <tr key={r.id} className="hover:bg-slate-50 align-top">
            <td className="px-4 py-2 font-medium text-slate-800 break-all">{r.query_value}</td>
            <td className="px-4 py-2 text-slate-500 capitalize">{r.query_type}</td>
            <td className="px-4 py-2 text-slate-500">{r.source_domain || '—'}</td>
            <td className="px-4 py-2 text-slate-700">{r.total_results.toLocaleString()}</td>
            <td className="px-4 py-2">
              <span className="text-xs px-2 py-0.5 rounded-md border text-emerald-700 bg-emerald-50 border-emerald-200">{r.owned_domains_found.length} owned</span>
              {r.connected_domains.length > r.owned_domains_found.length && (
                <span className="ml-1 text-xs text-slate-400">+{r.connected_domains.length - r.owned_domains_found.length} external</span>
              )}
            </td>
          </tr>
        ))}
        {rows.length === 0 && <EmptyRow span={5} text="No reverse-WHOIS connections." />}
      </Table>
    </div>
  );
}

function ClustersList({ clusters }: { clusters: FootprintCluster[] }) {
  if (clusters.length === 0) return <div className="card"><EmptyState compact icon={GitBranch} title="No clusters match" /></div>;
  return (
    <div className="space-y-3">
      {clusters.map((c) => (
        <div key={c.id} className={`card p-4 border-l-4 ${SEVERITY_STYLES[c.severity]?.split(' ').find((x) => x.startsWith('border')) ?? 'border-slate-200'}`}>
          <div className="flex items-center gap-2 flex-wrap">
            <SeverityBadge severity={c.severity} />
            <span className="text-xs text-slate-500 uppercase tracking-wide">{c.signal_type.replace(/_/g, ' ')}</span>
            <span className="text-xs text-slate-400">· {c.domain_count} domains</span>
            <span className="font-mono text-sm text-slate-700 ml-auto truncate max-w-[50%]" title={c.signal_value}>{c.signal_value}</span>
          </div>
          {c.description && <p className="text-sm text-slate-600 mt-2">{c.description}</p>}
          {c.remediation && <p className="text-xs text-slate-500 mt-1"><span className="font-medium">Fix:</span> {c.remediation}</p>}
          <div className="flex flex-wrap gap-1 mt-2">
            {c.domains.slice(0, 20).map((d) => <span key={d} className="text-xs bg-slate-100 text-slate-600 rounded px-1.5 py-0.5">{d}</span>)}
            {c.domains.length > 20 && <span className="text-xs text-slate-400">+{c.domains.length - 20} more</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

function AlertsTable({ alerts }: { alerts: FootprintAlert[] }) {
  return (
    <div className="card overflow-hidden">
      <Table head={['Severity', 'Domain', 'Issue', 'Discovered']}>
        {alerts.map((a) => (
          <tr key={a.id} className="hover:bg-slate-50 align-top">
            <td className="px-4 py-2"><SeverityBadge severity={a.severity} /></td>
            <td className="px-4 py-2 font-medium">{a.domain_id ? <Link to={`/domains/${a.domain_id}`} className="text-brand-600 hover:text-brand-800">{a.domain_name}</Link> : a.domain_name}</td>
            <td className="px-4 py-2">
              <div className="font-medium text-slate-800">{a.title}</div>
              {a.description && <div className="text-xs text-slate-500 mt-0.5 max-w-xl">{a.description}</div>}
            </td>
            <td className="px-4 py-2 text-slate-500 text-xs whitespace-nowrap">{format(new Date(a.discovered_at), 'MMM d, yyyy')}</td>
          </tr>
        ))}
        {alerts.length === 0 && <EmptyRow span={4} text="No open alerts." />}
      </Table>
    </div>
  );
}

function WhoisTable({ rows }: { rows: FootprintWhoisRow[] }) {
  return (
    <div className="card overflow-hidden">
      <Table head={['Domain', 'Registrar', 'Name', 'Company', 'Privacy', 'NS']}>
        {rows.map((w) => (
          <tr key={w.id} className={`hover:bg-slate-50 ${!w.privacy_protected ? 'bg-red-50/40' : ''}`}>
            <td className="px-4 py-2 font-medium">{w.domain_id ? <Link to={`/domains/${w.domain_id}`} className="text-brand-600 hover:text-brand-800">{w.domain_name}</Link> : w.domain_name}</td>
            <td className="px-4 py-2 text-slate-600">{w.registrar || '—'}</td>
            <td className="px-4 py-2 text-slate-600">{w.registrant_name || '—'}</td>
            <td className="px-4 py-2 text-slate-600">{w.registrant_org || '—'}</td>
            <td className="px-4 py-2">
              {w.privacy_protected
                ? <span className="text-xs px-2 py-0.5 rounded-md border text-emerald-700 bg-emerald-50 border-emerald-200">Protected</span>
                : <span className="text-xs px-2 py-0.5 rounded-md border text-red-700 bg-red-50 border-red-200">Exposed</span>}
            </td>
            <td className="px-4 py-2 text-slate-400 text-xs font-mono truncate max-w-[160px]" title={w.nameservers.join(', ')}>{w.nameservers[0] || '—'}</td>
          </tr>
        ))}
        {rows.length === 0 && <EmptyRow span={6} text="No WHOIS data for this run." />}
      </Table>
    </div>
  );
}

function HistoryTable({ runs }: { runs: import('../types').FootprintRun[] }) {
  const statusDot: Record<string, string> = { completed: 'bg-emerald-500', running: 'bg-amber-500 animate-pulse', failed: 'bg-red-500' };
  return (
    <div className="card overflow-hidden">
      <Table head={['Type', 'Status', 'Domains', 'Exposed', 'Clusters', 'Score', 'Started']}>
        {runs.map((r) => (
          <tr key={r.id} className="hover:bg-slate-50">
            <td className="px-4 py-2 text-slate-600 capitalize">{r.audit_type.replace(/_/g, ' ')}</td>
            <td className="px-4 py-2"><span className="inline-flex items-center gap-1.5 text-slate-600"><span className={`h-2 w-2 rounded-full ${statusDot[r.status] ?? 'bg-slate-400'}`} />{r.status}</span></td>
            <td className="px-4 py-2 text-slate-600">{r.domains_checked.toLocaleString()}</td>
            <td className="px-4 py-2 text-slate-600">{r.domains_exposed.toLocaleString()}</td>
            <td className="px-4 py-2 text-slate-600">{r.clusters_found.toLocaleString()}</td>
            <td className="px-4 py-2 text-slate-600">{r.exposure_score}</td>
            <td className="px-4 py-2 text-slate-500 text-xs whitespace-nowrap">{format(new Date(r.started_at), "MMM d, yyyy 'at' h:mm a")}</td>
          </tr>
        ))}
        {runs.length === 0 && <EmptyRow span={7} text="No audit runs yet." />}
      </Table>
    </div>
  );
}

function Table({ head, children }: { head: string[]; children: React.ReactNode }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider border-b border-slate-200 bg-slate-50/50">
            {head.map((h) => <th key={h} className="px-4 py-2.5">{h}</th>)}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">{children}</tbody>
      </table>
    </div>
  );
}

function EmptyRow({ span, text }: { span: number; text: string }) {
  return <tr><td colSpan={span}><EmptyState compact title={text} /></td></tr>;
}
